// Components==============
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import storeTrainingSubscription from 'actions/training/subscription/store';
import indexTrainingSubscription from 'actions/training/subscription/index';
import indexTraining from 'actions/training/training/index';
import showTraining from 'actions/training/training/show';
import FilterMenu from 'components/Layout/FilterMenu/FilterMenu';
import Layout from 'components/Layout/Layout';
import Button from 'components/UI/Button/Button';
import Container from 'components/UI/Container/Container';
import NoContent from 'components/UI/NoContent/NoContent';
import SharecodeInput from 'components/UI/SharecodeInput/SharecodeInput';
import Title from 'components/UI/Title/Title';
import TrainingCard from 'components/UI/TrainingCard/TrainingCard';
import { useAlert } from 'hooks/useAlert';
import { useFilter } from 'hooks/useFilter';
import { useMediaQ } from 'hooks/useMediaQ';
import { usePermission } from 'hooks/usePermission';
import { useRedux } from 'hooks/useRedux';
import { useReduxAction } from 'hooks/useReduxAction';
import { useTranslate } from 'hooks/useTranslate';
import React, { useEffect } from 'react';
import {
  categoryFilter,
  publicationFilter,
  reduceFilter,
  searchFilter,
} from 'utilities/filters';
import { getDate } from 'utilities/getDate';
import './MyCourses.module.scss';
// =========================

export default function Courses() {
  const dispatch = useReduxAction();
  const translate = useTranslate();

  const userOrganisation = useRedux(
    (state) => state.authentication?.user.organisation.name || ''
  );

  useEffect(() => {
    dispatch(indexTraining());
    dispatch(indexTrainingSubscription({}));
  }, []);

  // Filter logic
  const { filterValues, setFilterValues, initial } = useFilter('my-courses');

  const f1 = searchFilter(filterValues[0].value as string, 'title');
  const f2 = categoryFilter(filterValues[1].value as number[]);
  const f3 = publicationFilter(
    filterValues[2].value as string,
    userOrganisation
  );

  const userId = useRedux((state) => state.authentication?.user?.id);

  const courses = useRedux((state) => {
    const data = state.training?.entities.allIds
      .map((id) => state.training?.entities.byId[id])
      .filter(
        (course) => course?.owner?.id === userId || course?.subscription_id
      )
      .sort((a, b) => getDate(b?.updated_at) - getDate(a?.updated_at));

    return reduceFilter([f1, f2, f3], data);
  });
  //

  const query = useMediaQ('min', 1024);
  const hasPermission = usePermission(['Docent', 'super-admin']);

  const alert = useAlert();

  function handleShareCodeSubmit(shareCode: string) {
    dispatch(storeTrainingSubscription({ shareCode }))
      .then((res: any) =>
        dispatch(showTraining({ trainingId: res.training_id })).then(() =>
          dispatch(indexTraining())
        )
      )
      .catch(() => {
        alert(translate('course.errors.could-not-add-training'));
      });
  }

  const loading = useRedux((state) => state.training?.loading);

  const publicCourses = courses?.filter(
    (course) =>
      course?.is_published && (!course?.completed || !course.sessions?.length)
  );
  const privateCourses = courses?.filter(
    (course) =>
      !course?.is_published && (!course?.completed || !course.sessions?.length)
  );
  const completedCourses = courses?.filter(
    (course) =>
      !!course?.subscription_id &&
      !!course?.completed &&
      !!course.sessions?.length
  );

  return (
    <Layout
      filter={
        <FilterMenu
          filterValues={filterValues}
          setFilterValues={setFilterValues}
          initial={initial}
        />
      }
    >
      <NoContent
        page="my-courses"
        loading={!!loading}
        hasContent={courses ? courses?.length > 0 : false}
        sharecode={<SharecodeInput handleSubmit={handleShareCodeSubmit} />}
      >
        <Container>
          <Title text="general.terms.my-courses" loading={loading} />

          <div styleName="top">
            {query && hasPermission && (
              <Button to="/werkplaats/training-bouwer">
                {translate('course.actions.create-course')}
                <FontAwesomeIcon icon={faPlus} />
              </Button>
            )}
            <SharecodeInput handleSubmit={handleShareCodeSubmit} />
          </div>
          <div styleName="page-wrapper">
            {!!publicCourses?.length && (
              <div styleName="wrapper">
                {publicCourses?.map((course) => (
                  <TrainingCard key={course?.id} training={course} />
                ))}
              </div>
            )}
            {!!privateCourses?.length && (
              <div>
                <Title text="course.terms.drafts" loading={false} />
                <div styleName="wrapper">
                  {privateCourses?.map((course) => (
                    <TrainingCard key={course?.id} training={course} />
                  ))}
                </div>
              </div>
            )}
            {!!completedCourses?.length && (
              <div>
                <Title text="course.terms.completed-courses" loading={false} />
                <div styleName="wrapper">
                  {completedCourses?.map((course) => (
                    <TrainingCard key={course?.id} training={course} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </Container>
      </NoContent>
    </Layout>
  );
}
